// Shared, side-effect-free evidence policy used by Finance Settings and payment validation.
// Version 2 owns pre-send requirements per payment type. Legacy post-payment rules
// remain separate: changing preparation policy must not waive Finance finalization.
const { canonicalDocumentRequirement, requirementDocumentTypes, evidenceTypeKey, documentTypesForEvidence } = require('./paymentDocumentTypes');
const VERSION = 2;
const DEFAULT_ATTACHMENT_TYPES = ['funding_agreement', 'EFT_form'];
const own = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);
const object = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const text = value => typeof value === 'string' ? value.trim() : '';
const key = value => text(value).toLowerCase().replace(/[^a-z0-9]+/g, '');
const safeKey = value => !['__proto__', 'constructor', 'prototype'].includes(value);
const list = value => {
  const seen = new Set();
  return (Array.isArray(value) ? value : []).map(text).filter(value => {
    const id = evidenceTypeKey(value);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};
const readList = (source, fields) => {
  const field = fields.find(name => own(source, name));
  return field ? list(source[field]) : [];
};
const payeeKey = value => {
  const id = key(value);
  const aliases = {
    participant: 'client', participantclient: 'client',
    traininginstitution: 'institution', educationalinstitution: 'institution',
    accreditedinstitution: 'institution', accreditededucationaltraininginstitution: 'institution',
    employerwagesubsidypartner: 'employer', wagesubsidypartner: 'employer',
    supplier: 'vendor', serviceprovider: 'vendor', childcareprovider: 'vendor',
    trainingrelatedsupplier: 'vendor', professionalbusinessservicesprovider: 'vendor',
    professionalservicesprovider: 'vendor', businessservicesprovider: 'vendor',
    communityorganization: 'other', nonprofitorganization: 'other', communitynonprofitorganization: 'other',
  };
  return aliases[id] || id;
};
const defaultRequirement = evidenceType => canonicalDocumentRequirement({
  evidenceType,
  delivery: DEFAULT_ATTACHMENT_TYPES.some(type => evidenceTypeKey(type) === evidenceTypeKey(evidenceType)) ? 'attach' : 'on_file',
  whenMissing: 'warn',
});
const mergeRequirements = (...sets) => {
  const result = new Map();
  sets.flat().forEach(input => {
    const raw = canonicalDocumentRequirement(input);
    const evidenceType = text(raw?.evidenceType);
    if (!evidenceType) return;
    const prior = result.get(evidenceTypeKey(evidenceType));
    const defaults = defaultRequirement(evidenceType);
    const current = {
      evidenceType: prior?.evidenceType || evidenceType,
      delivery: raw.delivery || defaults.delivery,
      whenMissing: raw.whenMissing || defaults.whenMissing,
    };
    if (raw.documentTypes) current.documentTypes = raw.documentTypes;
    if (prior?.delivery === 'attach') current.delivery = 'attach';
    if (prior?.whenMissing === 'block') current.whenMissing = 'block';
    result.set(evidenceTypeKey(evidenceType), current);
  });
  return [...result.values()];
};
const requiredTypes = source => readList(source, ['required', 'requiredEvidence', 'required_evidence']);
const optionalTypes = source => list(readList(source, ['optional', 'optionalEvidence', 'optional_evidence']).flatMap(documentTypesForEvidence));
const postPayTypes = source => readList(source, ['postPayRequired', 'post_pay_required', 'postPay']);
const requirementsOf = source => own(source, 'requirements')
  ? mergeRequirements(Array.isArray(source.requirements) ? source.requirements : [])
  : requiredTypes(source).map(defaultRequirement);
const ruleSet = (source, inheritedRequirements = []) => {
  source = object(source);
  // An explicit v2 list, including [], replaces any legacy shared pre-send policy.
  const requirements = own(source, 'requirements')
    ? requirementsOf(source)
    : mergeRequirements(inheritedRequirements, requirementsOf(source));
  return {
    requirements,
    required: requirements.map(rule => rule.evidenceType),
    optional: optionalTypes(source),
    postPayRequired: postPayTypes(source),
  };
};

function createDefaultPaymentEvidenceRuleSet() {
  return ruleSet({ requirements: DEFAULT_ATTACHMENT_TYPES.map(defaultRequirement) });
}

function normalizePaymentEvidenceRules(raw, paymentTypeCodes = []) {
  const source = object(raw);
  const isV2 = Number(source.version) >= VERSION;
  const baselineSource = object(source.baseline || source.baselineEvidence || source.baseline_evidence);
  const hasLegacyBaseline = ['baseline', 'baselineEvidence', 'baseline_evidence'].some(field => own(source, field));
  const inheritedRequirements = isV2 ? [] : hasLegacyBaseline
    ? requirementsOf(baselineSource)
    : createDefaultPaymentEvidenceRuleSet().requirements;
  const sourceTypes = object(source.paymentTypes || source.payment_types);
  const entries = new Map();
  Object.entries(sourceTypes).forEach(([code, value]) => {
    if (text(code) && safeKey(code)) entries.set(key(code), [text(code), object(value)]);
  });
  (Array.isArray(paymentTypeCodes) ? paymentTypeCodes : []).forEach(item => {
    const code = text(typeof item === 'string' ? item : item?.code);
    if (code && safeKey(code) && !entries.has(key(code))) {
      entries.set(key(code), [code, isV2 ? createDefaultPaymentEvidenceRuleSet() : {}]);
    }
  });
  const paymentTypes = {};
  entries.forEach(([code, value]) => {
    const normalized = ruleSet(value, inheritedRequirements);
    const scoped = object(value.payeeTypes || value.payee_types);
    const baselineScoped = isV2 ? {} : object(baselineSource.payeeTypes || baselineSource.payee_types);
    const payeeTypes = {};
    [...Object.entries(baselineScoped), ...Object.entries(scoped)].forEach(([rawPayee, scopedRule]) => {
      const payee = payeeKey(rawPayee);
      if (!payee || !safeKey(rawPayee)) return;
      const previous = payeeTypes[payee];
      const normalizedScope = ruleSet(scopedRule, previous?.requirements || []);
      if (previous) {
        normalizedScope.optional = list([...previous.optional, ...normalizedScope.optional]);
        normalizedScope.postPayRequired = list([...previous.postPayRequired, ...normalizedScope.postPayRequired]);
      }
      payeeTypes[payee] = normalizedScope;
    });
    if (Object.keys(payeeTypes).length) normalized.payeeTypes = payeeTypes;
    paymentTypes[code] = normalized;
  });
  return {
    version: VERSION,
    // Keep downstream shared post-payment settings; no pre-send baseline survives.
    baseline: {
      requirements: [], required: [], optional: optionalTypes(baselineSource),
      postPayRequired: postPayTypes(baselineSource),
    },
    paymentTypes,
  };
}

function resolvePaymentEvidenceRuleSet(raw, paymentType, payeeType) {
  const config = normalizePaymentEvidenceRules(raw, paymentType ? [paymentType] : []);
  const rule = Object.entries(config.paymentTypes).find(([code]) => key(code) === key(paymentType))?.[1];
  const scoped = rule?.payeeTypes?.[payeeKey(payeeType)];
  const requirements = mergeRequirements(rule?.requirements || [], scoped?.requirements || []);
  return {
    requirements,
    required: requirements.map(item => item.evidenceType),
    // Optional legacy catalogues never determine outgoing attachments.
    optional: [],
    postPayRequired: list([
      ...config.baseline.postPayRequired, ...(rule?.postPayRequired || []), ...(scoped?.postPayRequired || []),
    ]),
  };
}

function validatePaymentEvidenceRules(raw, { knownEvidenceTypes } = {}) {
  const errors = [];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { valid: false, errors: ['Evidence settings must be an object.'] };
  }
  if (own(raw, 'version') && raw.version !== VERSION) errors.push('Evidence settings version must be 2.');
  const known = Array.isArray(knownEvidenceTypes)
    ? new Set(knownEvidenceTypes.flatMap(item => documentTypesForEvidence(typeof item === 'string' ? item : item?.value)).map(evidenceTypeKey)) : null;
  const checkRule = (rawRule, path, allowPayees = true) => {
    if (!rawRule || typeof rawRule !== 'object' || Array.isArray(rawRule)) {
      errors.push(`${path} must be an object.`);
      return;
    }
    if (own(rawRule, 'requirements')) {
      if (!Array.isArray(rawRule.requirements)) errors.push(`${path}: requirements must be a list.`);
      else {
        const seen = new Set();
        rawRule.requirements.forEach((rule, index) => {
          const label = `${path}, requirement ${index + 1}`;
          const type = text(rule?.evidenceType);
          if (own(rule, 'documentTypes') && (!Array.isArray(rule.documentTypes) || !rule.documentTypes.length || rule.documentTypes.some(value => !text(value)) || evidenceTypeKey(rule.documentTypes[0]) !== evidenceTypeKey(type))) errors.push(`${label}: invalid document types.`);
          if (!type) errors.push(`${label}: choose an evidence type.`);
          else if (known && requirementDocumentTypes(rule).some(code => !known.has(evidenceTypeKey(code)))) errors.push(`${label}: unknown document type ${type}.`);
          if (type && seen.has(evidenceTypeKey(type))) errors.push(`${path}: evidence type ${type} is repeated.`);
          seen.add(evidenceTypeKey(type));
          if (!['attach', 'on_file'].includes(rule?.delivery)) errors.push(`${label}: choose Attach to email or On file is sufficient.`);
          if (!['warn', 'block'].includes(rule?.whenMissing)) errors.push(`${label}: choose Warn and allow sending or Block sending.`);
        });
      }
    }
    ['required', 'optional', 'postPayRequired', 'post_pay_required'].forEach(field => {
      if (own(rawRule, field) && (!Array.isArray(rawRule[field]) || rawRule[field].some(value => !text(value)))) {
        errors.push(`${path}: ${field} must be a list of evidence type codes.`);
      }
    });
    const payees = rawRule.payeeTypes || rawRule.payee_types;
    if (payees !== undefined) {
      if (!allowPayees || !payees || typeof payees !== 'object' || Array.isArray(payees)) {
        errors.push(`${path}: invalid payee-specific rules.`);
      } else {
        const seenPayees = new Set();
        Object.entries(payees).forEach(([payee, rule]) => {
          if (!text(payee) || !safeKey(payee) || seenPayees.has(payeeKey(payee))) errors.push(`${path}: invalid or duplicate payee type.`);
          seenPayees.add(payeeKey(payee));
          checkRule(rule, `${path} / ${payee}`, false);
        });
      }
    }
  };
  const paymentTypes = raw.paymentTypes || raw.payment_types;
  if (!paymentTypes || typeof paymentTypes !== 'object' || Array.isArray(paymentTypes)) {
    errors.push('Payment type evidence rules must be an object.');
  } else {
    const seen = new Set();
    Object.entries(paymentTypes).forEach(([code, rule]) => {
      if (!text(code) || !safeKey(code) || seen.has(key(code))) errors.push('Payment type evidence codes must be unique and non-empty.');
      seen.add(key(code));
      checkRule(rule, code);
    });
  }
  const baseline = raw.baseline || raw.baselineEvidence || raw.baseline_evidence;
  if (baseline !== undefined) {
    checkRule(baseline, 'Shared evidence');
    if (Number(raw.version) >= VERSION && (requiredTypes(baseline).length || requirementsOf(baseline).length || Object.keys(object(baseline.payeeTypes)).length)) {
      errors.push('Configure pre-send evidence on each payment type, not as shared defaults.');
    }
  }
  return { valid: errors.length === 0, errors };
}

module.exports = {
  PAYMENT_EVIDENCE_RULES_VERSION: VERSION,
  createDefaultPaymentEvidenceRuleSet,
  normalizePaymentEvidenceRules,
  resolvePaymentEvidenceRuleSet,
  validatePaymentEvidenceRules,
};
